import React, { useState, useEffect } from 'react';
import { Col, Card, Button, Modal, Form, Spinner, Row } from 'react-bootstrap';
import axios from '../utils/axios';
import { useNavigate, Link } from 'react-router-dom';
import './Videos.css';
import { useSelector } from 'react-redux';
import { getImageUrl } from '../utils/imageUtils'; // ✅ Import utilitaire images

const Playlist = () => {
  const [playlists, setPlaylists] = useState([]);
  const [products, setProducts] = useState([]); // ✅ Nouvel état pour les produits
  const [currentPage, setCurrentPage] = useState(1);
  const playlistsPerPage = 6;
  const [showModal, setShowModal] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [newPlaylistImage, setNewPlaylistImage] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(''); // ✅ Produit sélectionné
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  // ✅ Récupérer les produits de type 1 (formations en ligne)
  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      // Filtrer seulement les produits de type 1 (formations en ligne)
      const onlineProducts = response.data.products.filter(product => product.type === 1);
      setProducts(onlineProducts);
    } catch (error) {
      console.error('Error fetching products:', error.message);
    }
  };

  const fetchPlaylists = async () => {
    // Wait for userInfo to be available
    if (!userInfo) {
      return;
    }

    if (userInfo.isAdmin || userInfo.isOffice || userInfo.isFormateur) {
      try {
        const response = await axios.get('/api/playlists');
        setPlaylists(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
        console.error('Error fetching playlists:', error.message);
      }
    } else {
      navigate('/login');
    }
  };

  const handlePlaylistAdd = async () => {
    if (!selectedProduct) {
      setError('Veuillez sélectionner un produit');
      return;
    }

    if (userInfo && (userInfo.isAdmin || userInfo.isOffice || userInfo.isFormateur)) {
      const formData = new FormData();
      formData.append('name', newPlaylistName);
      formData.append('description', newPlaylistDescription);
      formData.append('product', selectedProduct); // ✅ Ajouter le produit
      if (newPlaylistImage) {
        formData.append('image', newPlaylistImage);
      }

      try {
        setLoading(true);
        setError('');
        await axios.post('/api/playlists', formData);
        fetchPlaylists();
        handleCloseModal();
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to add playlist. Please try again.');
        console.error('Error adding playlist:', error.message);
      } finally {
        setLoading(false);
      }
    } else {
      navigate('/login');
    }
  };

  const handlePlaylistDelete = async (playlistId) => {
    if (userInfo && (userInfo.isAdmin || userInfo.isOffice || userInfo.isFormateur)) {
      try {
        setLoading(true);
        await axios.delete(`/api/playlists/${playlistId}`);
        fetchPlaylists();
      } catch (error) {
        console.error('Error deleting playlist:', error.message);
      } finally {
        setLoading(false);
      }
    } else {
      navigate('/login');
    }
  };

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewPlaylistName('');
    setNewPlaylistDescription('');
    setNewPlaylistImage(null);
    setSelectedProduct('');
    setError('');
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Calculate the current playlists to display
  const indexOfLastPlaylist = currentPage * playlistsPerPage;
  const indexOfFirstPlaylist = indexOfLastPlaylist - playlistsPerPage;
  const currentPlaylists = playlists.slice(
    indexOfFirstPlaylist,
    indexOfLastPlaylist
  );

  const totalPages = Math.ceil(playlists.length / playlistsPerPage);

  useEffect(() => {
    // Wait for userInfo before making API calls
    if (!userInfo) {
      return;
    }

    if (userInfo.isAdmin || userInfo.isOffice || userInfo.isFormateur) {
      fetchPlaylists();
      fetchProducts(); // ✅ Charger les produits
    } else {
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  return (
    <Col>
      <h1>Gestion des Playlists</h1>
      <Button variant='success' onClick={handleShowModal}>
        Ajouter une Playlist
      </Button>
      {error && <p className='error text-danger'>{error}</p>}
      
      <Row className="mt-4">
        {currentPlaylists.map((playlist) => (
          <Col key={playlist._id} md={4} className="mb-4">
            <Card className="h-100">
              {/* ✅ Correction affichage image */}
              <Card.Img
                variant='top'
                src={getImageUrl(playlist.image)}
                alt={playlist.name}
                style={{
                  width: '100%',
                  height: '250px',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  e.target.src = '/images/placeholder.jpg';
                }}
              />
              <Card.Body className="d-flex flex-column">
                <Card.Title>{playlist.name}</Card.Title>
                <Card.Text>{playlist.description}</Card.Text>
                
                {/* ✅ Afficher le produit associé */}
                {playlist.product && (
                  <Card.Text className="text-muted small">
                    <strong>Produit associé:</strong> {playlist.product.name}
                  </Card.Text>
                )}
                
                <div className="mt-auto">
                  {userInfo.isAdmin && (
                    <Button
                      variant='danger'
                      size="sm"
                      onClick={() => handlePlaylistDelete(playlist._id)}
                      className="me-2"
                    >
                      Supprimer
                    </Button>
                  )}
                  <Link
                    to={
                      userInfo.isFormateur
                        ? `/formateur/playlists/${playlist._id}/videos`
                        : `/admin/playlists/${playlist._id}/videos`
                    }
                  >
                    <Button variant='info' size="sm">
                      Voir les Vidéos
                    </Button>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {playlists.length === 0 && (
        <div className="text-center mt-5">
          <p>Aucune playlist créée.</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='pagination mt-4'>
          {[...Array(totalPages).keys()].map((page) => (
            <Button
              key={page + 1}
              variant={currentPage === page + 1 ? 'primary' : 'outline-primary'}
              onClick={() => handlePageChange(page + 1)}
              className="me-2"
            >
              {page + 1}
            </Button>
          ))}
        </div>
      )}

      {/* Modal for adding a playlist */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter une Playlist</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId='playlistName' className="mb-3">
              <Form.Label>Nom de la Playlist *</Form.Label>
              <Form.Control
                type='text'
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                placeholder="Entrez le nom de la playlist"
              />
            </Form.Group>
            
            <Form.Group controlId='playlistDescription' className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type='text'
                value={newPlaylistDescription}
                onChange={(e) => setNewPlaylistDescription(e.target.value)}
                placeholder="Entrez la description"
              />
            </Form.Group>
            
            {/* ✅ Sélection du produit */}
            <Form.Group controlId='playlistProduct' className="mb-3">
              <Form.Label>Produit associé *</Form.Label>
              <Form.Select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
              >
                <option value="">Sélectionnez un produit</option>
                {products.map((product) => (
                  <option key={product._id} value={product._id}>
                    {product.name} (Type: {product.type})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Seuls les produits de type 1 (formations en ligne) sont affichés
              </Form.Text>
            </Form.Group>
            
            <Form.Group controlId='playlistImage' className="mb-3">
              <Form.Label>Image de la Playlist</Form.Label>
              <Form.Control
                type='file'
                accept="image/*"
                onChange={(e) => setNewPlaylistImage(e.target.files[0])}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseModal}>
            Annuler
          </Button>
          <Button
            variant='primary'
            onClick={handlePlaylistAdd}
            disabled={loading || !newPlaylistName || !selectedProduct}
          >
            {loading && (
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
                className="me-2"
              />
            )}
            Créer la Playlist
          </Button>
        </Modal.Footer>
      </Modal>
    </Col>
  );
};

export default Playlist;